/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import axios from 'axios';
import {FETCH_FFC_ALL_VERSION_DATA, FETCH_DATA_FOR_APP_PICKER, FETCH_SINGLE_APP_PLAN_CVS_DATA, FETCH_FFC_APP_PICKER_DATA, CHECK_HAS_THOR} from '../types';
import {
	CCPP_PLAN,
	ALL_APPS_PLAN,
	CCPPN_PLAN,
	SUBSTANCE_COLLECTION_PLAN,
	SUBSTANCE_TEXTURING_PLAN
} from '../constants/AppPicker/appPickerConstants';
import {singleAppPlans} from '../constants/appsConstants';
import {KEYS_FOR_SERVICES} from '../utils/postIMSFetchAppsData';
import ThorConnectionUtil from '../utils/ThorConnectionUtil';
import {getContentfulPath} from '../utils/contentfulDataPathUtil';
import {getAEMPath} from '../utils/aemPathUtil';
import AppsUtil from '../utils/AppsUtil';
import RestApiUtil from '../utils/RestApiUtil';
import getConfigApps from '../config';

function useNull(e) {
	console.log(e);
	return null;
}

function getContentUrl(config: string, locale: string) {
	if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
		return `${AppsUtil.getAEMDomain()}${getAEMPath(config, locale)}`;
	}
	return `${AppsUtil.getCVSDomain()}${getContentfulPath(config, locale)}`;
}


function getCVSDataForAppPickerPromise(inputLocale: string, starterPlan: string) {
	let CVSDataUrl;
	let CVSDataFallbackUrl;
	if (starterPlan === ALL_APPS_PLAN) {
		CVSDataUrl = getContentUrl('getAppPickerConfig', inputLocale);
		CVSDataFallbackUrl = '/assets/apps/allAppsAppPicker/en/allAppPickerFallbackData.json';
	}
	else if (starterPlan === CCPP_PLAN) {
		CVSDataUrl = getContentUrl('getAppPickerConfigCcpp', inputLocale);
		CVSDataFallbackUrl = '/assets/apps/ccppAppPicker/en/appPickerFallback.json';
	}
	else if (starterPlan === CCPPN_PLAN) {
		CVSDataUrl = getContentUrl('getAppPickerConfigCcppn', inputLocale);
		CVSDataFallbackUrl = '/assets/apps/ccppnAppPicker/en/appPickerFallback.json';
	}
	else if (starterPlan === SUBSTANCE_TEXTURING_PLAN) {
		CVSDataUrl = getContentUrl('getAppPickerConfigSubstanceTexturing', inputLocale);
		CVSDataFallbackUrl = '/assets/apps/substanceTexturingAppPicker/en/appPickerFallback.json';
	}
	else if (starterPlan === SUBSTANCE_COLLECTION_PLAN) {
		CVSDataUrl = getContentUrl('getAppPickerConfigSubstanceCollection', inputLocale);
		CVSDataFallbackUrl = '/assets/apps/substanceCollectionAppPicker/en/appPickerFallback.json';
	}
	else if (singleAppPlans.includes(starterPlan)) {
		CVSDataUrl = getContentUrl(`getAppPickerConfig-${starterPlan}`, inputLocale);
		CVSDataFallbackUrl = `/assets/apps/singleApp/${starterPlan}/en/appPickerFallback.json`;
	}
	return [
		axios.get(CVSDataUrl, {timeout: 5000}).catch(useNull),
		axios.get(CVSDataFallbackUrl).catch(useNull),
	];
};

function getIntentDataForAppPickerPromise() {
	const appAffinityEndpoint = `${getConfigApps('servers').IntentServer}/v1/users/me/intents`;
	return axios.get(appAffinityEndpoint, {headers: RestApiUtil.getHeaders(true, false)}).catch(useNull);
};

function getFFCFetchPromiseArr(sapCodesToFetch) {
	const ffcFetchPromiseArr = [];
	for (let i = 0; i < sapCodesToFetch.length; i++) {
		if (!AppsUtil.allVersionsFFCProductList[sapCodesToFetch[i]]) {
			if (!AppsUtil.isFFCFallback) {
				ffcFetchPromiseArr.push(RestApiUtil.fetchFFCAllVersionData(sapCodesToFetch[i]));
			}
			else {
				ffcFetchPromiseArr.push(RestApiUtil.fetchFFCAllVersionDataFallback(sapCodesToFetch[i]));
			}
		}
		else {
			ffcFetchPromiseArr.push(true);
		}
	}
	return ffcFetchPromiseArr;
}

function processFFCFetchStatus(sapCodesToFetch: Array<string>, ffcDataFetchStatus: Array<boolean>) {
	const sapCodeDataFetchStatus = [];
	for (let i = 0; i < sapCodesToFetch.length; i++) {
		const sapCode = sapCodesToFetch[i];
		sapCodeDataFetchStatus.push({
			sapCode,
			status: ffcDataFetchStatus[i]
		});
		if (ffcDataFetchStatus[i] && (!AppsUtil.appsFFCProductList || !AppsUtil.appsFFCProductList[sapCode])) {
			if (!AppsUtil.appsFFCProductList) {
				AppsUtil.appsFFCProductList = {};
			}
			AppsUtil.appsFFCProductList[sapCode] = AppsUtil.allVersionsFFCProductList[sapCode][AppsUtil.allVersionsFFCProductList[sapCode].length - 1];
		}
	}
	return sapCodeDataFetchStatus;
}

export const getDataForAppPicker = (imsData: Object, inputLocale: string, starterPlan: string) => async(dispatch: Function) => {
	AppsUtil.setImsData(imsData);
	const allPromisesArray = getCVSDataForAppPickerPromise(inputLocale, starterPlan);
	if (!AppsUtil.isMobileOrTab()) {
		allPromisesArray.push(ThorConnectionUtil.checkForThor().catch(useNull));
	}
	else {
		allPromisesArray.push(Promise.resolve(undefined));
	}
	if (AppsUtil.hasFeature('temp_cch_7467')) {
		allPromisesArray.push(getIntentDataForAppPickerPromise());
	}
	return Promise.all(allPromisesArray).then(axios.spread((appPickerContentfulData, appPickerFallbackData, hasThor, intentData) => {
		dispatch({
			type: FETCH_DATA_FOR_APP_PICKER,
			payload: {appPickerContentfulData, appPickerFallbackData, hasThor, inputLocale, intentData}
		});
	}));
};

export const getSingleAppPostPurchaseCVSData = (inputLocale: string, starterPlan: string) => async(dispatch: Function) => {
	const allPromisesArray = getCVSDataForAppPickerPromise(inputLocale, starterPlan);
	return Promise.all(allPromisesArray).then(axios.spread((appPickerContentfulData, appPickerFallbackData) => {
		dispatch({
			type: FETCH_SINGLE_APP_PLAN_CVS_DATA,
			payload: {appPickerContentfulData, appPickerFallbackData, inputLocale}
		});
	}));
};

function getFFCProductPromise() {
	const configObject = {
		channel: 'ccm,services,mobileApps',
		headers: {
			withAuth: true,
			withAppId: true
		},
		config: {
			allVersion: false
		},
		isCCD: false
	};

	return RestApiUtil.getFFCProductPromise(configObject).catch(useNull);
}

export const getFFCDataForAppPicker = (sapCodesToFetchFirst: Array<string>, sapCodesToFetchNext: Array<string>, preFetchedData: Function = () => null) => async(dispatch: Function) => {
	// Fetch default category sapcode data and all sapcode latest version data
	const ffcFirstFetchPromiseArr = getFFCFetchPromiseArr(sapCodesToFetchFirst);
	ffcFirstFetchPromiseArr.push(
		preFetchedData(KEYS_FOR_SERVICES.FFC_LATEST) || getFFCProductPromise()
	);
	ffcFirstFetchPromiseArr.push(
		RestApiUtil.getFFCFallbackProductPromise('latest', true).catch(useNull),
	);
	ffcFirstFetchPromiseArr.push(RestApiUtil.fetchFFCFallbackProductsJson());
	return Promise.all(ffcFirstFetchPromiseArr).then((resp) => {
		/*
			The resp can be split four ways:
			1. The last component is data for forming WAM URL in case of fallback
			2. The second last component is FFC fallback data
			3. The third last part if FFC data
			4. And the rest of the beginning part is FFC all version data fetch status
    	 */
		const ffcFallbackProductsData = resp.splice(-1, 1)[0];
		const ffcFallbackData = resp.splice(-1, 1)[0];
		let ffcData = resp.splice(-1, 1)[0];
		const ffcDataFetchStatus = resp;

		if (!ffcData) {
			AppsUtil.isFFCFallback = true;
			if (ffcFallbackData && ffcFallbackProductsData) {
				ffcData = ffcFallbackData;
				AppsUtil.processAndStoreFFCFallbackProducts(ffcFallbackProductsData);
			}
		}
		else {
			AppsUtil.isFFCFallback = false;
		}
		if (AppsUtil.isFFCFallback) {
			dispatch({
				type: CHECK_HAS_THOR,
				payload: {hasThor: false}
			});
		}

		dispatch({
			type: FETCH_FFC_APP_PICKER_DATA,
			payload: {ffcData}
		});
		const sapCodeDataFetchStatus = processFFCFetchStatus(sapCodesToFetchFirst, ffcDataFetchStatus);
		dispatch({
			type: FETCH_FFC_ALL_VERSION_DATA,
			payload: {sapCodeDataFetchStatus}
		});

		// Fetch the rest of the sapcodes data
		const ffcNextFetchPromiseArr = getFFCFetchPromiseArr(sapCodesToFetchNext);
		return Promise.all(ffcNextFetchPromiseArr).then((ffcDataFetchStatus) => {
			const sapCodeDataFetchStatus = processFFCFetchStatus(sapCodesToFetchNext, ffcDataFetchStatus);
			dispatch({
				type: FETCH_FFC_ALL_VERSION_DATA,
				payload: {sapCodeDataFetchStatus}
			});
		});
	});
};
